import React, { HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';

import PropTypes from 'prop-types';
import { CSSProp } from 'styled-components';

import Overlay from '@adsk/alloy-react-overlay';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, {
  stylePropType,
  StylableComponent,
  flattenStyle,
} from '@adsk/alloy-react-theme';

import NoAutocompleteResults from './NoAutocompleteResults';
import { stripUnknownProps } from '../../helpers/internalHelpers';
import { GetMenuProps } from '../../types';

export type MenuProps = StylableComponent<HTMLUListElement> & {
  isOpen: boolean;
  isLoading?: boolean;
  isAutocomplete?: boolean;
  getMenuProps: GetMenuProps;
  width?: number;
  renderWrapper?: (
    props: HTMLAttributes<HTMLDivElement>,
  ) => ReactElement | null;
  renderFooter?: () => ReactNode;
  renderLoading?: () => ReactNode;
};

const unusedProps = ['selectItem', 'isGroupHeader', 'highlightedIndex'];

const scrollStyle: CSSProp = {
  position: 'relative',
  overflowY: 'auto',
};

const LoadingSpinner = () => (
  <div
    css={{
      display: 'flex',
      justifyContent: 'center',
      margin: `${theme.spacing.medium}px 0px`,
    }}
  >
    <ProgressRing />
  </div>
);

const Menu = React.forwardRef<HTMLUListElement, MenuProps>(
  (
    {
      style = {},
      width,
      isOpen,
      isLoading = false,
      isAutocomplete = false,
      getMenuProps,
      renderWrapper,
      renderFooter,
      renderLoading = () => <LoadingSpinner />,
      children,
      ...props
    },
    ref,
  ) => {
    const showFooter = isOpen && !!renderFooter;

    const wrapperStyle: CSSProp = {
      ...(!isOpen && {
        height: 0,
      }),
    };

    const parentStyle: CSSProp = [
      {
        boxShadow: theme.shadows.highElevation,
        backgroundColor: theme.colors.white,
        maxHeight: 300,
        maxWidth: 'calc(100% + 70px)',
        width,
        borderRadius: theme.borderRadius.medium,
      },
      style,
    ];

    const {
      ref: containerRef,
      style: containerStyle,
      ...menuProps
    } = getMenuProps();

    const strippedMenuProps = stripUnknownProps(menuProps, unusedProps);
    const strippedProvidedProps = stripUnknownProps(props, unusedProps);

    const itemsOrMessage =
      React.Children.toArray(children).length > 0 ? (
        children
      ) : (
        <NoAutocompleteResults />
      );

    const renderMenu = ({
      internalStyle,
    }: { internalStyle?: CSSProp } = {}) => (
      <ul
        ref={ref}
        css={[
          theme.typography.labelMedium,
          {
            listStyleType: 'none',
            userSelect: 'none',
            outline: 'none',
            padding: 0,
            margin: 0,
          },
          internalStyle,
        ]}
        {...strippedMenuProps}
        {...strippedProvidedProps}
      >
        {isOpen &&
          (isLoading
            ? renderLoading()
            : isAutocomplete
              ? itemsOrMessage
              : children)}
        <div tabIndex={0} />
      </ul>
    );

    const renderContent = ({ contentStyle }: { contentStyle: CSSProp }) =>
      renderWrapper
        ? renderWrapper({
            style: flattenStyle([contentStyle, wrapperStyle]),
            children: renderMenu(),
          })
        : renderMenu({ internalStyle: contentStyle });

    return (
      <Overlay.Container
        data-testid={'Dropdown__menu'}
        ref={containerRef as Ref<HTMLDivElement>}
        style={containerStyle as CSSProp}
      >
        {showFooter ? (
          <div
            css={[
              {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              },
              parentStyle,
            ]}
          >
            {renderContent({ contentStyle: scrollStyle })}
            {renderFooter()}
          </div>
        ) : (
          renderContent({ contentStyle: [scrollStyle, parentStyle] })
        )}
      </Overlay.Container>
    );
  },
);

Menu.displayName = 'Menu';

Menu.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The menu width */
  width: PropTypes.number,
  /** The content of the Menu */
  children: PropTypes.node,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Function to get props applied to the <ul> element.  This is returned from the dropdown hook */
  getMenuProps: PropTypes.func.isRequired,
  /**
   * Render a component that will wrap the <ul> element, but still be contained within the overlay.
   * Use this is to enable virtualization
   */
  renderWrapper: PropTypes.func,
  /** Render a component that will appear at the bottom of the menu */
  renderFooter: PropTypes.func,
  /** Render a component that will appear when isLoading is true */
  renderLoading: PropTypes.func,
};

export default Menu;
